import { useEffect, useState } from 'react'
import { Money } from '../../atoms'

import {
  StyledDeliveryDate,
  StyledPriceContainer,
  StyledSelectionContainer,
  StyledTitle,
  StyledToggleButton,
  StyledToggleButtonGroup,
  StyledType,
} from './styles/DeliveryDateSelector.style'
import { Alert } from '../../molecules/Alert'
import { getMissionsDiscount, selectedMissionsTypes } from '../../../common/missions'
import { useAppSelector } from '../../../common/hooks'
import { dispatch } from '../../../store'
import { scheduledDeliveryActions } from '../../../store/slices/scheduleDelivery'
import { DeliveryDate, getScheduleDeliveryDates } from '../../../services/ordersApi'
import { DEFAULT_DELIVERY_FEE, MOCK_DELIVERY_FEE } from '../../../common/constants'

interface DeliveryDateSelectorProps {
  hasPartnerProducts: boolean
}

const numberOfDaysOptions = 5

const calculateFee = (status: string): number => {
  if (status === 'Unavailable') return DEFAULT_DELIVERY_FEE
  if (status === 'FreeDay') return 0
  if (status === 'Paid') return DEFAULT_DELIVERY_FEE
  return DEFAULT_DELIVERY_FEE
}

export const DeliveryDateSelector = ({ hasPartnerProducts }: DeliveryDateSelectorProps) => {
  const { cart, missions, merchantCode } = useAppSelector((state) => ({
    cart: state.cart,
    missions: state.missions,
    merchantCode: state.session.merchantCode,
  }))

  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [deliveryMissionComplete, setdeliveryMissionComplete] = useState<boolean>(false)
  const [deliveryDates, setDeliveryDates] = useState<DeliveryDate[]>([])

  const handleSelect = (day: string, disabled: boolean, status: string, daysToDelivery: number) => {
    if (disabled) return

    const fee = calculateFee(status)
    setSelectedDate(day)
    dispatch(scheduledDeliveryActions.setScheduledDelivery({ fee, daysToDelivery }))
  }

  const renderStatusLabel = (status: string) => {
    if (status === 'Unavailable') return 'Indisponível'
    if (status === 'Paid')
      return deliveryMissionComplete ? 'Missão Frete Grátis' : <Money amount={MOCK_DELIVERY_FEE} />
    if (status === 'FreeDay')
      return deliveryMissionComplete ? 'Missão Frete Grátis' : 'Dia de Frete Grátis'
    return ''
  }

  const alertMessage =
    'Seu pedido possui um prazo de entrega maior, pois possui produtos de parceiros.'

  const additionalMessage =
    'Precisa receber antes? Por favor, remova os itens com a etiqueta “Parceiro”.'

  useEffect(() => {
    if (selectedMissionsTypes(missions)?.includes('FREE_SHIPPING')) {
      const discountAmount = getMissionsDiscount(missions, cart, DEFAULT_DELIVERY_FEE)
      setdeliveryMissionComplete(!!discountAmount)
    } else {
      setdeliveryMissionComplete(false)
    }
  }, [cart, missions])

  useEffect(() => {
    if (!selectedDate) return

    const selected = deliveryDates.find((d) => d.day === selectedDate)
    if (!selected) return

    const fee = calculateFee(selected.status)

    dispatch(
      scheduledDeliveryActions.setScheduledDelivery({
        fee,
        daysToDelivery: selected.daysToDelivery,
      })
    )
  }, [deliveryMissionComplete, selectedDate])

  useEffect(() => {
    const fetchDeliveryDates = async () => {
      try {
        const data = await getScheduleDeliveryDates(merchantCode, hasPartnerProducts)
        setDeliveryDates(data)
      } catch (error) {
        console.error('Erro ao carregar datas de entrega:', error)
      }
    }

    fetchDeliveryDates()
  }, [cart, hasPartnerProducts])

  return (
    <StyledSelectionContainer>
      <StyledTitle>Selecione a data de entrega:</StyledTitle>

      {hasPartnerProducts && (
        <Alert mainMessage={alertMessage} additionalMessage={additionalMessage} />
      )}

      <StyledToggleButtonGroup value={selectedDate} exclusive>
        {deliveryDates
          .slice(0, numberOfDaysOptions)
          .map(({ day, weekday, status, daysToDelivery }) => {
            const disabled = status === 'Unavailable'
            const selected = selectedDate === day
            return (
              <StyledToggleButton
                key={day}
                value={day}
                disabled={disabled}
                selected={selected}
                onClick={() => handleSelect(day, disabled, status, daysToDelivery)}
                data-testid={`delivery-date-${day}`}
              >
                <StyledType>
                  <StyledDeliveryDate selected={selected} disabled={disabled}>
                    Até {weekday.toLowerCase()} ({day})
                  </StyledDeliveryDate>
                </StyledType>

                <StyledPriceContainer
                  selected={selected}
                  disabled={disabled}
                  freeShipping={
                    status === 'FreeDay' || (deliveryMissionComplete && status === 'Paid')
                  }
                >
                  {renderStatusLabel(status)}
                </StyledPriceContainer>
              </StyledToggleButton>
            )
          })}
      </StyledToggleButtonGroup>
    </StyledSelectionContainer>
  )
}
